import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { uploadInitialComplaintsData } from '../../firebase/dataUploader'; // Import the uploader
import './AdminComplaints.css'; // We will create this for styling

const AdminComplaints = () => {
    const [data, setData] = useState({
        reportTitle: '',
        ComplaintData: [],
        trendData: [],
        annualTrendData: [],
        escalationData: [],
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });

    const docRef = doc(db, 'legalPages', 'complaintsData');

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setData(docSnap.data());
                } else {
                    setError('No complaints data found. A new document will be created on save.');
                    // The initial state already provides a default structure.
                }
            } catch (err) {
                console.error("Error fetching complaints data:", err);
                setError('Failed to load complaints data. Showing empty fields.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Generic handler to update state
    const handleChange = (e, section, index, field) => {
        const { value } = e.target;
        const newData = { ...data };

        // Handle nested array data
        if (index !== null) {
            const isNumeric = !isNaN(value) && value.trim() !== '';
            newData[section][index][field] = isNumeric ? Number(value) : value;
        } else {
            // Handle top-level fields like reportTitle
            newData[section] = value;
        }
        
        setData(newData);
    };
    
    // Save data to Firestore
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await setDoc(docRef, data);
            setSuccess('Data saved successfully!');
        } catch (err) {
            console.error("Error saving data:", err);
            setError('Failed to save data. Please try again.');
        } finally {
            setSaving(false);
        }
    };
    
    const handleInitialUpload = async () => {
        if (!window.confirm("Are you sure you want to upload and overwrite with the initial seed data? This should only be done once for setup.")) {
            return;
        }
        setUploading(true);
        setUploadStatus({ message: '', type: '' });
        const result = await uploadInitialComplaintsData();
        if (result.success) {
            setUploadStatus({ message: result.message, type: 'success' });
            // Refresh data on the page after successful upload
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setData(docSnap.data());
            }
        } else {
            setUploadStatus({ message: result.message, type: 'danger' });
        }
        setUploading(false);
    };

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}><Spinner animation="border" /></div>;
    }

    if (!data) {
        return <Alert variant="warning">Data is not available. Please try again later.</Alert>
    }

    return (
        <Container fluid className="admin-complaints-container">
            <Row className="justify-content-center">
                <Col lg={10} xl={8}>
                    <h1 className="admin-page-title">Edit Complaints Data</h1>
                    
                    {/* One-time Uploader Section */}
                    <Card className="mb-4 admin-card bg-light-warning">
                        <Card.Header as="h5">One-Time Data Setup</Card.Header>
                        <Card.Body>
                            <Card.Text>
                                If this is the first time setting up the page, click this button to upload the initial data from the code to the database. This will overwrite any existing data for the complaints page.
                            </Card.Text>
                            <Button
                                variant="warning"
                                disabled={uploading}
                                onClick={handleInitialUpload}
                            >
                                {uploading ? <Spinner as="span" animation="border" size="sm" /> : 'Upload Initial Seed Data'}
                            </Button>
                            {uploadStatus.message && (
                                <Alert variant={uploadStatus.type} className="mt-3">
                                    {uploadStatus.message}
                                 </Alert>
                            )}
                        </Card.Body>
                    </Card>

                    <p className="admin-page-description">
                        Modify the values in the tables below. The "Grand Total" rows are calculated automatically and are not editable. Click "Save Changes" at the bottom to update the public page.
                    </p>

                    {/* Report Title Editor */}
                    <Card className="mb-4 admin-card">
                        <Card.Header as="h5">Report Details</Card.Header>
                        <Card.Body>
                            <Form.Group>
                                <Form.Label>Report Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.reportTitle || ''}
                                    onChange={(e) => handleChange(e, 'reportTitle', null, null)}
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>
                    
                    {/* All Tables will go here */}
                    {/* Example for one table, repeat for others */}
                    <Card className="mb-4 admin-card">
                        <Card.Header as="h5">Monthly Complaints Summary</Card.Header>
                        <Card.Body>
                            <Table responsive striped bordered hover>
                                {/* Similar thead as public page */}
                                <thead>
                                    <tr>
                                        <th>Received from</th>
                                        <th>Pending last month</th>
                                        <th>Received</th>
                                        <th>Resolved</th>
                                        <th>Total Pending</th>
                                        <th>Pending &gt; 3 months</th>
                                        <th>Avg. Resolution time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.ComplaintData?.map((item, index) => (
                                        <tr key={index}>
                                            <td><Form.Control type="text" value={item.Received_from} onChange={(e) => handleChange(e, 'ComplaintData', index, 'Received_from')} /></td>
                                            <td><Form.Control type="number" value={item.Pending_at_the_end_of_last_month} onChange={(e) => handleChange(e, 'ComplaintData', index, 'Pending_at_the_end_of_last_month')} /></td>
                                            <td><Form.Control type="number" value={item.Received} onChange={(e) => handleChange(e, 'ComplaintData', index, 'Received')} /></td>
                                            <td><Form.Control type="number" value={item.Resolved} onChange={(e) => handleChange(e, 'ComplaintData', index, 'Resolved')} /></td>
                                            <td><Form.Control type="number" value={item.Total_Pending} onChange={(e) => handleChange(e, 'ComplaintData', index, 'Total_Pending')} /></td>
                                            <td><Form.Control type="number" value={item.Pending_Complaints_Over_3_months} onChange={(e) => handleChange(e, 'ComplaintData', index, 'Pending_Complaints_Over_3_months')} /></td>
                                            <td><Form.Control type="number" value={item.Average_Resolution_Time} onChange={(e) => handleChange(e, 'ComplaintData', index, 'Average_Resolution_Time')} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    {/* Monthly Trend Table */}
                    <Card className="mb-4 admin-card">
                        <Card.Header as="h5">Trend of Monthly Disposal</Card.Header>
                        <Card.Body>
                            <Table responsive striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th>Carried Forward</th>
                                        <th>Received</th>
                                        <th>Resolved</th>
                                        <th>Pending</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.trendData?.map((item, index) => (
                                        <tr key={index}>
                                            <td><Form.Control type="text" value={item.month} onChange={(e) => handleChange(e, 'trendData', index, 'month')} /></td>
                                            <td><Form.Control type="number" value={item.carried_forward} onChange={(e) => handleChange(e, 'trendData', index, 'carried_forward')} /></td>
                                            <td><Form.Control type="number" value={item.received} onChange={(e) => handleChange(e, 'trendData', index, 'received')} /></td>
                                            <td><Form.Control type="number" value={item.resolved} onChange={(e) => handleChange(e, 'trendData', index, 'resolved')} /></td>
                                            <td><Form.Control type="number" value={item.pending} onChange={(e) => handleChange(e, 'trendData', index, 'pending')} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                    
                    {/* Annual Trend Table */}
                     <Card className="mb-4 admin-card">
                        <Card.Header as="h5">Trend of Annual Disposal</Card.Header>
                        <Card.Body>
                            <Table responsive striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Year</th>
                                        <th>Carried Forward</th>
                                        <th>Received</th>
                                        <th>Resolved</th>
                                        <th>Pending</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.annualTrendData?.map((item, index) => (
                                        <tr key={index}>
                                            <td><Form.Control type="text" value={item.year} onChange={(e) => handleChange(e, 'annualTrendData', index, 'year')} /></td>
                                            <td><Form.Control type="number" value={item.carried_forward} onChange={(e) => handleChange(e, 'annualTrendData', index, 'carried_forward')} /></td>
                                            <td><Form.Control type="number" value={item.received} onChange={(e) => handleChange(e, 'annualTrendData', index, 'received')} /></td>
                                            <td><Form.Control type="number" value={item.resolved} onChange={(e) => handleChange(e, 'annualTrendData', index, 'resolved')} /></td>
                                            <td><Form.Control type="number" value={item.pending} onChange={(e) => handleChange(e, 'annualTrendData', index, 'pending')} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    {/* Escalation Matrix Table */}
                    <Card className="mb-4 admin-card">
                        <Card.Header as="h5">Escalation Matrix</Card.Header>
                        <Card.Body>
                            <Table responsive striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Designation</th>
                                        <th>Contact Person</th>
                                        <th>Address</th>
                                        <th>Contact No.</th>
                                        <th>Email</th>
                                        <th>Working Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.escalationData?.map((item, index) => (
                                        <tr key={index}>
                                            <td><Form.Control type="text" value={item.designation} onChange={(e) => handleChange(e, 'escalationData', index, 'designation')} /></td>
                                            <td><Form.Control type="text" value={item.contact_person} onChange={(e) => handleChange(e, 'escalationData', index, 'contact_person')} /></td>
                                            <td><Form.Control type="text" value={item.address} onChange={(e) => handleChange(e, 'escalationData', index, 'address')} /></td>
                                            <td><Form.Control type="text" value={item.contact_no} onChange={(e) => handleChange(e, 'escalationData', index, 'contact_no')} /></td>
                                            <td><Form.Control type="email" value={item.email} onChange={(e) => handleChange(e, 'escalationData', index, 'email')} /></td>
                                            <td><Form.Control type="text" value={item.working_hours} onChange={(e) => handleChange(e, 'escalationData', index, 'working_hours')} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    {/* Save Button and Feedback */}
                    <div className="save-button-container">
                         <Button
                            variant="primary"
                            size="lg"
                            disabled={saving}
                            onClick={handleSave}
                        >
                            {saving ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
                        </Button>
                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                        {success && <Alert variant="success" className="mt-3">{success}</Alert>}
                    </div>

                </Col>
            </Row>
        </Container>
    );
};

export default AdminComplaints; 